import { APITypesV1 } from "@cur8/api-client";
import { Visit } from "@cur8/rich-entity";
import { GENERIC_ECG_LABEL } from "lib/ecgScanLabel";
import { isEcho2Market } from "lib/market";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { limsManagedSamples, resolutionFor } from "lib/tubeStatus";
import { bloodWorkStatus } from "render/views/AssignVials/scanDecision";

// The scans that carry a status badge on the scan-progress list, in the order
// they appear there, so the warning reads in the same order the operator just
// scrolled past. Labels are duplicated from that list rather than shared: the
// list builds each row differently (links, deeplinks, per-row queries) and has
// no single label table to import.
const EXAMINATIONS = [
  { key: "skin", label: "Skin Scan" },
  { key: "tci", label: "Tissue Scan" },
  { key: "ecg", label: GENERIC_ECG_LABEL },
  { key: "cardio", label: "Cardio Scan" },
] as const;

/**
 * An open examination, with a key that stays put and a label that may not.
 *
 * The ECG is named after the device in the room, which arrives on its own
 * query, so callers keying anything off this list, an acknowledgement above
 * all, have to use the key. A late label would otherwise look like the list
 * itself had changed.
 */
export type OpenExamination = { key: string; label: string };

type Args = {
  visit: Visit | undefined;
  bloodSamples: APITypesV1.BloodSample[] | undefined;
  bloodWorkMode: APITypesV1.BloodWorkMode | undefined;
  ecgLabel?: string;
  // Required, unlike the label: leaving it off would quietly drop the cardio
  // row rather than raise a type error, and the caller is the only one that
  // knows which clinic the visit is in.
  countryCode: string | null | undefined;
};

/**
 * Examinations the scan-progress list is not showing as "Completed".
 *
 * Anything other than Finished counts, including a scan absent from the visit
 * altogether: the list draws no badge at all in that case, so an operator reads
 * it as outstanding and the warning has to agree with what they can see.
 *
 * The two blood rows are only on that list for the sites that do them, so they
 * only count under the same conditions: LIMS-managed samples for the draw
 * confirmation, barcode-assignment mode for blood work. Reporting either one
 * everywhere would fire on every visit at sites that never see the row.
 *
 * The draw confirmation reads the draw decision, not the lab verdict. A tube
 * the lab rejected was still drawn, so it is not work the operator has left to
 * do, and gates stay clear of lab status (AB#92441).
 *
 * Each source is skipped while its own query is in flight, so the screen never
 * claims work is outstanding on the strength of data it does not have yet.
 */
export function outstandingExaminations({
  visit,
  bloodSamples,
  bloodWorkMode,
  ecgLabel,
  countryCode,
}: Args): OpenExamination[] {
  const open: OpenExamination[] = [];

  if (visit != null) {
    for (const { key, label } of EXAMINATIONS) {
      // cardio/* is the Echo-2 modality, which has no FDA clearance in the US.
      // The row is off the scan-progress list there, so the warning must not
      // name it either. Same predicate the list uses, so the warning can never
      // demand a tick for a row the operator was not shown. The ECG is a
      // separate scan and is unaffected.
      if (key === "cardio" && !isEcho2Market(countryCode)) {
        continue;
      }

      if (
        readScanStateFromVisit(key, visit) !==
        APITypesV1.ImmutableScanState.Finished
      ) {
        open.push({
          key,
          label: key === "ecg" ? (ecgLabel ?? label) : label,
        });
      }
    }
  }

  if (bloodSamples != null) {
    const tubes = limsManagedSamples(bloodSamples);
    if (
      tubes.length > 0 &&
      !tubes.every((sample) => resolutionFor(sample) !== "pending")
    ) {
      open.push({
        key: "blood-draw",
        label: "Blood draw confirmation",
      });
    }

    if (
      bloodWorkMode === APITypesV1.BloodWorkMode.BarcodeAssignment &&
      bloodWorkStatus(bloodSamples) !== "complete"
    ) {
      open.push({ key: "blood-work", label: "Blood Work" });
    }
  }

  return open;
}
